// src/pages/DJPublicPage.tsx
import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import "./DJPublicPage.css";
import { FaInstagram, FaChevronDown, FaChevronUp, FaExternalLinkAlt } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";

interface DJProfile {
  name: string;
  email: string;
  slug: string;
  avatarUrl?: string;
  pricePerRequest: number;
  instagram?: string;
  currency?: "usd" | "eur" | string;
  links?: string[];
  bio?: string;
}

interface Track {
  id: string;
  name: string;
  artists: { name: string }[];
  preview_url?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = { usd: "$", eur: "€" };
const getSymbol = (code?: string) => (code ? CURRENCY_SYMBOLS[code.toLowerCase()] || "$" : "$");

const normalizeUrl = (v: string) => {
  const s = (v || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z]+:\/\//i.test(s)) return s;
  return `https://${s}`;
};

const DJPublicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [dj, setDj] = useState<DJProfile | null>(null);
  const { user } = useContext(AuthContext);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Track[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);

  const [amount, setAmount] = useState(1);
  const [comment, setComment] = useState("");
  const [showLinks, setShowLinks] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get(`/users/${slug}`).then((res) => setDj(res.data));
  }, [slug]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get(`/spotify/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(res.data.tracks || []);
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleSelectTrack = (track: Track) => {
    setSelectedTrack(track);
    setSearchQuery(track.name);
    setSearchResults([]);
  };

  const handleSubmit = () => {
    if (!selectedTrack || amount < 1) {
      return alert("Fill all required fields.");
    }
    console.log("Order submitted:", { dj: dj?.slug, track: selectedTrack, amount, comment });
  };

  if (!dj) return <p style={{ textAlign: "center" }}>Loading DJ...</p>;

  const isOwnProfile = user?.slug === dj.slug;
  const symbol = getSymbol(dj.currency || "usd");
  const handle = (dj.instagram || "").replace(/^@+/, "");
  const instaUrl = handle ? `https://instagram.com/${handle}` : "";

  const cleanedLinks = Array.isArray(dj.links)
    ? dj.links.map(normalizeUrl).filter((x) => !!x)
    : [];

  return (
    <div className="dj-public-container">
      <div className="dj-card-public">
        {dj.avatarUrl ? (
          <img
            src={dj.avatarUrl.startsWith("http") ? dj.avatarUrl : `https://api.requesta.app${dj.avatarUrl}`}
            alt="Avatar"
            className="dj-avatar"
          />
        ) : (
          <div className="avatar-placeholder">{dj.name.charAt(0).toUpperCase()}</div>
        )}

        <h2 className="dj-name-public">{dj.name}</h2>
        {dj.bio && (
          <p className="dj-bio-public">{dj.bio}</p>
        )}
        <p className="dj-price-public">
          Price per request: <span>{symbol} {dj.pricePerRequest || 1}</span>
        </p>

        {handle && (
          <a
            href={instaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-icon-link"
            title={`@${handle}`}
            aria-label={`Open Instagram @${handle}`}
          >
            <FaInstagram size={28} />
          </a>
        )}

        {cleanedLinks.length > 0 && (
          <div className="links-section">
            <button
              className="links-toggle"
              onClick={() => setShowLinks((s) => !s)}
              aria-expanded={showLinks}
              aria-controls="dj-links-list"
            >
              <span>Links</span>
              {showLinks ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {showLinks && (
              <ul id="dj-links-list" className="links-list-public">
                {cleanedLinks.map((u, i) => (
                  <li key={i}>
                    <a href={u} target="_blank" rel="noopener noreferrer">
                      {u}
                      <FaExternalLinkAlt size={12} style={{ marginLeft: 6 }} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!isOwnProfile && (
          <button className="request-button" onClick={() => (window.location.href = `/dj/${dj.slug}/request`)}>
            Make a Request
          </button>
        )}
      </div>
    </div>
  );
};

export default DJPublicPage;
